import { AdmPlan, SubjectType } from "@custom-types/base-types";
import { createSlice, createEntityAdapter } from "@reduxjs/toolkit";
import {
  BaseEntityAdapter,
  BaseEntityState,
  FetchingStatus,
} from "@store/store-helper";
import { updatePlan, fetchPlans, deletePlan } from "@store/plans/plans-thunks";

export const plansAdapter: BaseEntityAdapter<AdmPlan> = createEntityAdapter({
  selectId: (plan) => plan.id,
});

/** State for plans slice */
interface PlansState extends BaseEntityState<AdmPlan> {
  /** The type of the subject which the plan belongs to */
  subjectType: SubjectType | null;

  /** Async operation statuses for plans slice */
  fetchingPlan: {
    /** The status of fetching all plans */
    allPlans: FetchingStatus;

    /** The status of updating the plan */
    updatingPlan: FetchingStatus;
  };
}

/** Initial state for plans slice */
const initialPlansState: PlansState = {
  ...plansAdapter.getInitialState(),

  /** By default, no subject type is selected */
  subjectType: null,

  fetchingPlan: {
    allPlans: "uninitialized",
    updatingPlan: "uninitialized",
  },
};

/** Slice to manage plans state */
const plansSlice = createSlice({
  name: "plans",
  initialState: initialPlansState,
  reducers: {
    /** Resets the plans state to its initial values */
    resetPlansState: () => initialPlansState,
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchPlans.pending, (state) => {
        state.fetchingPlan.allPlans = "pending";
      })
      .addCase(fetchPlans.rejected, (state) => {
        state.fetchingPlan.allPlans = "rejected";
      })
      .addCase(fetchPlans.fulfilled, (state, action) => {
        state.subjectType = action.meta.arg.subjectType;

        plansAdapter.setAll(state, action.payload);
        state.fetchingPlan.allPlans = "succeeded";
      })

      .addCase(updatePlan.pending, (state) => {
        state.fetchingPlan.updatingPlan = "pending";
      })
      .addCase(updatePlan.fulfilled, (state, action) => {
        state.fetchingPlan.updatingPlan = "succeeded";
        plansAdapter.setOne(state, action.payload);
      })
      .addCase(updatePlan.rejected, (state) => {
        state.fetchingPlan.updatingPlan = "rejected";
      })

      .addCase(deletePlan.fulfilled, (state, action) => {
        plansAdapter.removeOne(state, action.meta.arg.planId);
      });
  },

  selectors: {
    /** Selector to get the entire plans state */
    plansStateSelector: (state) => state,
  },
});

export const { resetPlansState } = plansSlice.actions;

export const { plansStateSelector } = plansSlice.selectors;

export const plansReducer = plansSlice.reducer;
